const minute = 60_000;
const hour = 60 * minute;
const day = 24 * hour;

// Fixed three-letter months: locale data varies ("Sept" in some en-GB builds).
const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function dayMonth(date: Date): string {
  return `${date.getDate()} ${months[date.getMonth()] ?? ""}`;
}

/**
 * Hairline relative time: "Just now", "12 min ago", "3 hr ago", "Yesterday",
 * "3 days ago" within a week, then the day and month ("4 Aug"), with the year
 * only when it differs from the current one.
 */
export function relativeTime(value: string, now: number = Date.now()): string {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "Unknown";
  const elapsed = Math.max(0, now - time);
  if (elapsed < minute) return "Just now";
  if (elapsed < hour) return `${Math.floor(elapsed / minute)} min ago`;
  if (elapsed < day) return `${Math.floor(elapsed / hour)} hr ago`;
  const days = Math.floor(elapsed / day);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const date = new Date(time);
  return date.getFullYear() === new Date(now).getFullYear()
    ? dayMonth(date)
    : `${dayMonth(date)} ${date.getFullYear()}`;
}

/**
 * The list's short age: "now", "12m", "3h", "2d" within a week, then the day
 * and month as in `relativeTime`.
 */
export function shortRelativeTime(
  value: string,
  now: number = Date.now(),
): string {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "";
  const elapsed = Math.max(0, now - time);
  if (elapsed < minute) return "now";
  if (elapsed < hour) return `${Math.floor(elapsed / minute)}m`;
  if (elapsed < day) return `${Math.floor(elapsed / hour)}h`;
  const days = Math.floor(elapsed / day);
  if (days < 7) return `${days}d`;
  return relativeTime(value, now);
}

/** The exact moment for tooltips and status lines: "5 Oct, 14:02". */
export function exactTime(value: string): string {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "an unknown time";
  const date = new Date(time);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${dayMonth(date)}, ${hours}:${minutes}`;
}

/**
 * Time left before a future moment: "In 40 min", "In 5 hr", "Tomorrow",
 * "In 6 days", then the day and month. A past moment reads "Expired".
 */
export function relativeUntil(value: string, now: number = Date.now()): string {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "Unknown";
  const remaining = time - now;
  if (remaining <= 0) return "Expired";
  if (remaining < hour)
    return `In ${Math.max(1, Math.floor(remaining / minute))} min`;
  if (remaining < day) return `In ${Math.floor(remaining / hour)} hr`;
  const days = Math.floor(remaining / day);
  if (days === 1) return "Tomorrow";
  if (days < 7) return `In ${days} days`;
  const date = new Date(time);
  return date.getFullYear() === new Date(now).getFullYear()
    ? dayMonth(date)
    : `${dayMonth(date)} ${date.getFullYear()}`;
}
